import React, {
  FC,
  MouseEventHandler,
  useCallback,
  useState,
  useRef,
  useEffect,
} from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import dynamic from "next/dynamic";
import { Button, Theme, styled, useMediaQuery } from "@mui/material";
import { useTranslation } from "next-i18next";
import { useContainerDimensions } from "../../hooks/use-container-dimensions";

export type GalleryPreviewPhoto = {
  title: string;
  resource: string;
};

export type GalleryPreviewProps = {
  onPhotoClick?: (position?: number) => void;
  onClikShowAll?: React.MouseEventHandler;
  photos: GalleryPreviewPhoto[];
};

export const GalleryPreview: FC<GalleryPreviewProps> = ({
  onPhotoClick,
  onClikShowAll,
  photos,
}) => {
  const { t } = useTranslation();
  const mobileVersion = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down("sm")
  );
  const [currentSlide, setCurrentSlide] = useState(0);

  const photosToRender = photos.slice(0, mobileVersion ? photos.length : 5);

  let gridTemplateAreas;
  let gridTemplateColumns;

  switch (photosToRender.length) {
    case 1:
      gridTemplateAreas = `"photo0"`;
      gridTemplateColumns = `1fr`;
      break;
    case 2:
      gridTemplateAreas = `"photo0 photo1"`;
      gridTemplateColumns = `1fr 1fr`;
      break;
    case 3:
      gridTemplateAreas = `"photo0 photo1 " "photo0 photo2"`;
      gridTemplateColumns = `1fr 1fr`;
      break;
    case 4:
      gridTemplateAreas = `"photo0 photo1" "photo2 photo3"`;
      gridTemplateColumns = `1fr 1fr`;
      break;
    default:
      gridTemplateAreas = `"photo0 photo1 photo2" "photo0 photo3 photo4"`;
      gridTemplateColumns = `2fr 1fr 1fr`;
      break;
  }

  const handleSlideChange = useCallback(
    (index: number) => {
      setCurrentSlide(index);
      if (onPhotoClick) onPhotoClick(index);
    },
    [onPhotoClick]
  );

  const photosComponent = photosToRender.map((photo, index) => (
    <PhotoContainer
      key={`photo-${index}`}
      onClick={() => handleSlideChange(index)}
      resource={photo.resource}
      isMain={index === 0}
      style={{ gridArea: `photo${index}` }}
    />
  ));

  if (photosToRender.length === 0) return null;

  return (
    <Container>
      {mobileVersion ? (
        <div>
          <Slider lazyLoad="progressive" afterChange={setCurrentSlide}>
            {photosComponent}
          </Slider>
          {photos.length > 1 && (
            <span className="photos-counter">
              {currentSlide + 1} / {photosComponent.length}
            </span>
          )}
        </div>
      ) : (
        <div>
          <div
            className="gallery-photos"
            style={{ gridTemplateAreas, gridTemplateColumns }}
          >
            {photosComponent}
          </div>
          {photos.length > 1 && (
            <Button onClick={onClikShowAll} variant="contained">
              {t("show_all_photos")}
            </Button>
          )}
        </div>
      )}
    </Container>
  );
};

const Container = styled("div")`
  position: relative;
  overflow: hidden;

  .slick-slider {
    .slick-arrow {
      z-index: 100;
      &.slick-prev {
        left: 0.25rem;
      }
      &.slick-next {
        right: 0.25rem;
      }
    }
  }

  .photo {
    cursor: pointer;

    img {
      display: block;
      width: 100%;
      height: auto;
    }

    .overlay {
      pointer-events: none;
      position: absolute;
      z-index: 101;
      width: 100%;
      height: 100%;
      background-color: #000;
      opacity: 0;
      transition: opacity 0.3s ease-out;
    }

    :hover,
    :focus {
      .overlay {
        opacity: 0.1;
      }
    }
  }

  button {
    display: none;
    position: absolute;
    right: 2em;
    bottom: 2em;
  }

  .photos-counter {
    position: absolute;
    right: 1em;
    bottom: 1em;
    font-family: "Numans", Arial;
    font-size: 0.8em;
    color: #fff;
    background-color: rgba(0, 0, 0, 0.8);
    padding: 0.5em 1em;
    border-radius: 0.5em;
  }

  ${({ theme }) => theme.breakpoints.up("md")} {
    border-radius: 1em;
    margin: 0 auto;

    button {
      display: block;
    }

    .photos-counter {
      display: none;
    }

    .gallery-photos {
      display: grid;
      gap: 0.25rem;
      height: 50vh;

      .photo {
        overflow: hidden;
        position: relative;
        background-position: 50% 50%;
        background-repeat: no-repeat;

        img {
          object-fit: cover;
          position: absolute;
          width: 100%;
          height: 100%;
        }
      }
    }
  }
`;

const PhotoContainer = styled(
  ({
    isMain,
    resource,
    ...props
  }: {
    isMain: boolean;
    resource: string;
    onClick?: MouseEventHandler;
    style?: React.CSSProperties;
  }) => {
    const [containerRef, dimensions] = useContainerDimensions();
    const [isVisible, setIsVisible] = useState(false);
    const observerRef = useRef<IntersectionObserver | null>(null);

    useEffect(() => {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setIsVisible(true);
              observerRef.current?.disconnect();
            }
          });
        },
        {
          threshold: 0.1, // Cargar la imagen cuando el 10% sea visible
        }
      );
      if (containerRef.current) {
        observerRef.current.observe(containerRef.current);
      }
      return () => {
        observerRef.current?.disconnect();
      };
    }, [containerRef]);

    return (
      <div ref={containerRef} {...props}>
        {isVisible && (
          <Photo
            style={{
              backgroundImage: `url(/api/documents/${resource}?fit=cover&width=${dimensions.width}&height=${dimensions.height}&format=webp)`,
            }}
          />
        )}
        <div className="overlay" />
      </div>
    );
  }
)`
  position: relative;
  cursor: pointer;
  overflow: hidden;

  ${({ theme }) => theme.breakpoints.down("md")} {
    height: 0;
    padding-bottom: 100%;
  }

  .overlay {
    pointer-events: none;
    position: absolute;
    z-index: 101;
    width: 100%;
    height: 100%;
    background-color: #000;
    opacity: 0;
    transition: opacity 0.3s ease-out;
  }

  :hover,
  :focus {
    .overlay {
      opacity: 0.1;
    }
  }
`;

const Photo = styled("div")`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  background-size: cover;
  background-position: center center;
`;
